import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

import { env } from "~/env.mjs";
import { readAttributionCookie } from "~/lib/attribution-cookie";
import { pickAttributionParams } from "~/lib/attribution-params";
import {
  isPosthogProxyEnabled,
  POSTHOG_PROXY_PATH,
} from "~/lib/posthog-proxy.mjs";
import { type Analytics } from "./AnalyticsType";
import { buildAttributionRegistration } from "./attribution-registration";

const posthogProxied = isPosthogProxyEnabled(env.NEXT_PUBLIC_POSTHOG_PROXY);

// Earlier releases registered these permanently, so every later event carried
// the first visit's values. A stored `$referrer` also replaces the one
// posthog-js records per session.
const LEGACY_PERMANENT_PROPS = [
  "$referrer",
  "$referring_domain",
  "utm_content_source",
];

if (typeof window !== "undefined" && env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: posthogProxied
      ? POSTHOG_PROXY_PATH
      : env.NEXT_PUBLIC_POSTHOG_HOST,
    // When proxied, posthog-js can't infer the PostHog app origin from
    // api_host (needed for the toolbar and links back to the PostHog UI).
    ...(posthogProxied ? { ui_host: "https://us.posthog.com" } : {}),
    person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
    capture_pageview: false,
    session_recording: {
      captureCanvas: {
        // accepts a number between 0 and 12
        // if not set locally, when canvas recording is enabled, remote config sets this to 4
        canvasFps: 1,
        // accepts a decimal number between 0 and 1 as a string
        // if not set locally, when canvas recording is enabled, remote config sets this to "0.4"
        canvasQuality: "0.2",
      },
    },
    loaded: (ph) => {
      // if (process.env.NODE_ENV === "development") ph.debug();
      try {
        for (const key of LEGACY_PERMANENT_PROPS) {
          if (ph.get_property(key) !== undefined) {
            ph.unregister(key);
          }
        }

        const attribution = getAttributionData();
        // The cookie is first touch; only the URL describes this visit.
        const landing = pickAttributionParams(
          new URLSearchParams(window.location.search).entries(),
        );
        const { once, session } = buildAttributionRegistration(
          attribution,
          landing,
        );

        if (Object.keys(session).length > 0) {
          ph.register_for_session(session);
        }

        if (Object.keys(attribution).length > 0) {
          // Add data to the Sentry context for better debugging if attribution parsing fails
          Sentry.setContext("attribution", attribution);
          // Register all attribution parameters (utm_*, gclid, etc.)
          ph.register_once({ ...once, initial_attribution: attribution });
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    },
  });
}

/**
 * Retrieves attribution data from the cookie set by middleware.
 * This includes ref (referrer domain), utm_* parameters, and ad click IDs.
 */
function getAttributionData(): Record<string, string> {
  if (typeof document === "undefined") return {};

  return readAttributionCookie(document.cookie);
}

export { posthog };

export class PosthogAnalytics implements Analytics {
  constructor() {}

  setUser(
    userId: string,
    userProperties?: { email?: string | null; username?: string | null },
  ) {
    // posthog-js takes set / set-once as two positional args (the React Native
    // SDK takes one object with $set / $set_once instead — see
    // apps/mobile/src/lib/analytics/identity.ts). signup_platform is set-once
    // so a mobile-first user keeps "mobile" after they later sign in here.
    posthog.identify(
      userId,
      { ...userProperties, last_platform: "web" },
      { signup_platform: "web" },
    );
  }

  trackEvent(
    eventInput: string,
    eventProperties: Record<string, any> | undefined,
  ) {
    posthog.capture(eventInput, eventProperties);
  }
}
